import type { ReflectionEntry, ReflectionNote } from "@couples/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { HeartHandshake, Plus } from "lucide-react";
import { useMemo, useState } from "react";

import OverlayShell from "../components/OverlayShell";
import QueryState from "../components/QueryState";
import ReflectionCard from "../components/ReflectionCard";
import ReflectionEntrySheet from "../components/ReflectionEntrySheet";
import { errorMessageOf, useAuth } from "../lib/auth";
import { colorOfUser, DEFAULT_PARTNER_COLOR, DEFAULT_SELF_COLOR } from "../lib/color";
import { useConfirm } from "../lib/confirm";
import { todayDateKey } from "../lib/dates";
import { fetchMembers } from "../lib/media/members";
import { fetchCoupleProfiles } from "../lib/profileItems";
import { queryKeys } from "../lib/queryKeys";
import { createEntry, deleteEntry, fetchEntries } from "../lib/reflection/entries";
import { createNote, fetchNotes, resolveNote } from "../lib/reflection/notes";
import { useToast } from "../lib/toast";

export default function Reflection({ onClose }: { onClose: () => void }) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const selfId = session?.user.id;

  // "new" = 新規作成。ReflectionNote = そのノートへ自分の振り返りを追記
  const [composing, setComposing] = useState<"new" | ReflectionNote | null>(null);
  const [saving, setSaving] = useState(false);

  const members = useQuery({ queryKey: queryKeys.members, queryFn: fetchMembers });
  const profiles = useQuery({ queryKey: queryKeys.coupleProfiles, queryFn: fetchCoupleProfiles });
  const notes = useQuery({ queryKey: queryKeys.reflectionNotes, queryFn: fetchNotes });
  const entries = useQuery({ queryKey: queryKeys.reflectionEntries, queryFn: fetchEntries });

  const entriesByNote = useMemo(() => {
    const map = new Map<string, ReflectionEntry[]>();
    for (const entry of entries.data ?? []) {
      const list = map.get(entry.noteId) ?? [];
      list.push(entry);
      map.set(entry.noteId, list);
    }
    return map;
  }, [entries.data]);

  const partnerId = useMemo(
    () => [...(members.data?.keys() ?? [])].find((id) => id !== selfId),
    [members.data, selfId],
  );

  const nameOf = (userId: string) => members.data?.get(userId) ?? "?";
  const colorOf = (userId: string) => colorOfUser(profiles.data, userId, selfId);

  const isLoading = notes.isPending || entries.isPending;

  async function refreshNotes() {
    await queryClient.invalidateQueries({ queryKey: queryKeys.reflectionNotes });
  }
  async function refreshEntries() {
    await queryClient.invalidateQueries({ queryKey: queryKeys.reflectionEntries });
  }

  async function handleCreate(input: {
    title: string;
    occurredOn: string;
    cause: string;
    nextStep: string;
  }) {
    setSaving(true);
    try {
      const note = await createNote({ title: input.title, occurredOn: input.occurredOn });
      await createEntry({ noteId: note.id, cause: input.cause, nextStep: input.nextStep });
      await refreshNotes();
      await refreshEntries();
      setComposing(null);
      toast("ふりかえりを書きました。");
    } catch (e) {
      console.error("ふりかえりの保存に失敗", e);
      toast("ふりかえりの保存に失敗しました。", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddEntry(input: { noteId: string; cause: string; nextStep: string }) {
    setSaving(true);
    try {
      await createEntry(input);
      await refreshEntries();
      setComposing(null);
      toast("ふりかえりを書きました。");
    } catch (e) {
      console.error("ふりかえりの保存に失敗", e);
      toast("ふりかえりの保存に失敗しました。", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteEntry(entry: ReflectionEntry) {
    const ok = await confirm({
      message: "このふりかえりを消します。\n※この操作は取り消せません。",
      confirmLabel: "消す",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteEntry(entry.id);
      await refreshEntries();
      toast("削除しました。");
    } catch (e) {
      toast(errorMessageOf(e), "error");
    }
  }

  async function handleResolve(note: ReflectionNote) {
    if (selfId === undefined) return;
    try {
      await resolveNote(note.id, selfId);
      await refreshNotes();
      toast("解決済みにしました。");
    } catch (e) {
      toast(errorMessageOf(e), "error");
    }
  }

  return (
    <OverlayShell
      title="ふりかえりメモ"
      icon={HeartHandshake}
      onClose={onClose}
      actions={
        <button
          type="button"
          onClick={() => {
            setComposing("new");
          }}
          aria-label="ふりかえりを書く"
        >
          <Plus className="size-5 text-[var(--color-sub)]" aria-hidden />
        </button>
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <QueryState
          isPending={isLoading}
          isError={notes.isError || entries.isError}
          errorMessage="ふりかえりメモの取得に失敗しました。"
          isEmpty={notes.isSuccess && entries.isSuccess && notes.data.length === 0}
          emptyMessage={
            <>
              まだふりかえりメモがありません。
              <br />
              喧嘩やすれ違いがあったら、落ち着いてから残しておきましょう。
            </>
          }
          emptyClassName="py-10 text-center text-sm"
        />
        {notes.isSuccess &&
          entries.isSuccess &&
          notes.data.map((note) => {
            const noteEntries = entriesByNote.get(note.id) ?? [];
            const selfEntry = noteEntries.find((entry) => entry.authorId === selfId);
            const partnerEntry = noteEntries.find((entry) => entry.authorId === partnerId);
            return (
              <ReflectionCard
                key={note.id}
                note={note}
                selfEntry={selfEntry}
                partnerEntry={partnerEntry}
                selfName={selfId !== undefined ? nameOf(selfId) : "自分"}
                partnerName={partnerId !== undefined ? nameOf(partnerId) : "相手"}
                selfColor={selfId !== undefined ? colorOf(selfId) : DEFAULT_SELF_COLOR}
                partnerColor={partnerId !== undefined ? colorOf(partnerId) : DEFAULT_PARTNER_COLOR}
                onDeleteEntry={(entry) => void handleDeleteEntry(entry)}
                onWriteOwn={() => {
                  setComposing(note);
                }}
                onResolve={() => void handleResolve(note)}
              />
            );
          })}
      </div>

      {composing !== null && (
        <ReflectionEntrySheet
          existingNote={composing === "new" ? undefined : composing}
          partnerName={partnerId !== undefined ? nameOf(partnerId) : "相手"}
          todayKey={todayDateKey()}
          busy={saving}
          onCreate={(input) => void handleCreate(input)}
          onAddEntry={(input) => void handleAddEntry(input)}
          onClose={() => {
            setComposing(null);
          }}
        />
      )}
    </OverlayShell>
  );
}
